import React from 'react';
import {
  WorkListRenderer,
  WorkDetailRenderer,
  WorkCreatedRenderer,
  WorkDashboardRenderer,
  WorkStandupRenderer,
  WorkDiffRenderer,
  WorkBurndownRenderer,
  WorkLeaderboardRenderer,
  WorkForecastRenderer,
} from './WorkRenderers';
import {
  AdminListRenderer,
  AdminDetailRenderer,
  AdminSuccessRenderer,
  AdminStatsRenderer,
} from './AdminRenderers';
import { JsonPreviewRenderer } from './JsonPreviewRenderer';

export interface RendererProps {
  toolCall: any;
  darkMode: boolean;
}

// Normalize tool name across admin-chat (tool_name) and persona-chat (identity.tool_name) shapes.
export function getToolName(toolCall: any): string {
  return toolCall?.identity?.tool_name ?? toolCall?.tool_name ?? 'unknown';
}

// Extract the tool's result payload:
//   - admin-chat: result is a JSON-stringified `{success, result, ...}` envelope
//   - persona-chat: result is already a parsed object/scalar
// Unwraps the `{result}` envelope when present.
export function getResultData(toolCall: any): any {
  const raw = toolCall?.result;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed?.result ?? parsed;
    } catch {
      return raw;
    }
  }
  if (raw && typeof raw === 'object' && 'result' in raw && 'success' in raw) {
    return raw.result;
  }
  return raw;
}

export const TOOL_RENDERERS: Record<string, React.FC<RendererProps>> = {
  // Work tools
  admin_list_work_items: WorkListRenderer,
  admin_get_work_item: WorkDetailRenderer,
  admin_create_work_item: WorkCreatedRenderer,
  admin_update_work_item: WorkDetailRenderer,
  admin_delete_work_item: WorkDiffRenderer,
  admin_add_entity_ref: WorkDetailRenderer,
  admin_resolve_entity: WorkListRenderer,
  admin_scaffold_goal: WorkCreatedRenderer,
  admin_work_summary: WorkDashboardRenderer,
  admin_work_attention: WorkDashboardRenderer,
  admin_standup_summary: WorkStandupRenderer,
  admin_reassign_tasks: WorkDiffRenderer,
  admin_bulk_update_status: WorkDiffRenderer,
  admin_work_burndown: WorkBurndownRenderer,
  admin_work_leaderboard: WorkLeaderboardRenderer,
  admin_work_forecast: WorkForecastRenderer,

  // Admin user/system tools
  admin_list_users: AdminListRenderer,
  admin_get_user_detail: AdminDetailRenderer,
  admin_create_user: AdminSuccessRenderer,
  admin_change_user_status: AdminSuccessRenderer,
  admin_reset_password: AdminSuccessRenderer,
  admin_assign_role: AdminSuccessRenderer,
  admin_invite_user: AdminSuccessRenderer,
  admin_list_invites: AdminListRenderer,
  admin_revoke_invite: AdminSuccessRenderer,
  admin_list_templates: AdminListRenderer,
  admin_create_template: AdminSuccessRenderer,
  admin_get_system_stats: AdminStatsRenderer,
  admin_view_audit_log: AdminListRenderer,
  admin_get_version: AdminDetailRenderer,
  admin_summarize_audit_log: AdminDetailRenderer,
  admin_get_platform_info: AdminDetailRenderer,
  admin_browse_api: AdminListRenderer,
  admin_count_entities: AdminStatsRenderer,
};

export function getRenderer(toolName: string): React.FC<RendererProps> {
  return TOOL_RENDERERS[toolName] ?? JsonPreviewRenderer;
}

export { JsonPreviewRenderer };
